import { type AxiosRequestConfig } from "axios";
import { type CountItemsDTO } from "src/types/countItemDto";
import { EventItemsDTO } from "src/types/eventItemDto";
import { type WeatherItemsDTO } from "src/types/weatherItemDto";

import axiosErrorHandler from "./apiError.handler";
import http from "./axios";
import { type LocationModel } from "../types/location";

export async function apiGetLocationsTree(): Promise<ODataResponse<LocationModel>> {
  try {
    const { data } = await http.get(`Location`);
    return data;
  } catch (error: unknown) {
    throw axiosErrorHandler(error, "Error while loading locations");
  }
}

export async function apiGetLocationFiles(locationId: string | undefined): Promise<LocationFileDto[]> {
  try {
    const requestConfig: AxiosRequestConfig<{ locationId: string }> = {
      params: {
        locationId,
      },
    };
    const { data }: { data: LocationFileDto[] } = await http.get(`LocationUtility/Files`, requestConfig);
    return data;
  } catch (error: unknown) {
    throw axiosErrorHandler(error, "Error while getting files for location");
  }
}

export async function apiGetLocationFile(fileId: string | undefined, download: boolean): Promise<Blob> {
  try {
    const requestConfig: AxiosRequestConfig<{ fileId: string }> = {
      params: {
        fileId,
      },
      responseType: download ? "blob" : undefined,
      maxBodyLength: Number.POSITIVE_INFINITY,
      timeout: Number.POSITIVE_INFINITY,
    };
    const { data }: { data: Blob } = await http.get(`LocationUtility/File`, requestConfig);
    return data;
  } catch (error: unknown) {
    throw axiosErrorHandler(error, "Error while getting files for location");
  }
}

export async function apiGetCountData(id: string, filter: string): Promise<CountItemsDTO> {
  try {
    const params = new URLSearchParams();
    params.append("$filter", filter);
    params.append("$select", "InCount, OutCount, LocationId, Granularity, MeasureTime");
    const { data } = await http.get<CountItemsDTO>(`Count(${id})`, { params });
    return data;
  } catch (error: unknown) {
    throw axiosErrorHandler(error, "Error while loading locations");
  }
}

export async function apiGetWeatherForecastData(id: string, filter: string): Promise<WeatherItemsDTO> {
  try {
    const params = new URLSearchParams();
    params.append("$filter", filter);
    const { data } = await http.get<WeatherItemsDTO>(`WeatherForecast(${id})`, { params });
    return data;
  } catch (error: unknown) {
    throw axiosErrorHandler(error, "Error while loading weather forecast");
  }
}

export async function apiGetEventForecastData(_id: string, filter: string): Promise<EventItemsDTO> {
  try {
    const params = new URLSearchParams();
    params.append("$filter", filter);
    const { data } = await http.get<EventItemsDTO>(`Event?$top=1&$orderby=MeasureTime asc`, { params });
    return data;
  } catch (error: unknown) {
    throw axiosErrorHandler(error, "Error while loading event forecast");
  }
}
